import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, RefreshCcw, LogOut, Mail, Phone, Building2, Globe2, Inbox, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "klairys.admin.token";

export default function AdminPage() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  // Auto-attempt login on mount if token saved
  useEffect(() => {
    if (token) attemptAuth(token, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function attemptAuth(tk, silent = false) {
    setChecking(true);
    try {
      await axios.get(`${API}/admin/ping`, {
        headers: { "X-Admin-Token": tk },
      });
      try { localStorage.setItem(STORAGE_KEY, tk); } catch {}
      setToken(tk);
      setAuthed(true);
      if (!silent) toast.success("Authentifié.");
      fetchLeads(tk);
    } catch (e) {
      setAuthed(false);
      if (!silent) toast.error("Token invalide.");
    } finally {
      setChecking(false);
    }
  }

  async function fetchLeads(tk = token) {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads?limit=200`, {
        headers: { "X-Admin-Token": tk },
      });
      setLeads(data.leads || []);
    } catch (e) {
      toast.error("Échec du chargement.");
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setToken("");
    setAuthed(false);
    setLeads([]);
    setActive(null);
  }

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.email, l.company, l.sector, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, query]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
        <Toaster position="bottom-right" richColors />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            attemptAuth(token);
          }}
          className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_24px_60px_-25px_rgba(15,23,42,0.25)]"
          data-testid="admin-login-form"
        >
          <div className="w-12 h-12 rounded-xl bg-[#005BEA]/10 flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-[#005BEA]" />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900 mb-2">
            Console klairys.fr
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Saisissez votre <span className="font-semibold text-slate-700">ADMIN_TOKEN</span> pour consulter les leads.
          </p>
          <Input
            type="password"
            data-testid="admin-token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="h-11"
            autoFocus
          />
          <Button
            type="submit"
            disabled={!token || checking}
            data-testid="admin-login-submit"
            className="w-full mt-4 bg-[#005BEA] hover:bg-[#0048b9] text-white h-11 rounded-full font-semibold disabled:opacity-60"
          >
            {checking ? "Vérification…" : "Se connecter"}
          </Button>
          <a
            href="/"
            className="block text-center mt-4 text-xs text-slate-500 hover:text-slate-700"
          >
            ← Retour au site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Toaster position="bottom-right" richColors />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#005BEA] flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 leading-none">
                klairys.fr
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase font-medium text-slate-500 mt-0.5">
                Admin · Leads
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLeads()}
              data-testid="admin-refresh"
              className="rounded-full"
              disabled={loading}
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {leads.length} lead{leads.length > 1 ? "s" : ""}
            </h2>
            <p className="text-slate-500 mt-1 text-sm">
              Triés par date de réception (plus récent d'abord).
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher (nom, email, secteur…)"
              data-testid="admin-search"
              className="pl-9 h-10 rounded-full bg-white"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {leads.length === 0
                ? "Aucun lead reçu pour l'instant."
                : "Aucun résultat pour cette recherche."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* List */}
            <ul className="lg:col-span-5 space-y-3">
              {filtered.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => setActive(l)}
                    data-testid={`lead-${l.id}`}
                    className={`w-full text-left bg-white border rounded-xl p-5 hover:border-slate-300 transition-all ${
                      active?.id === l.id ? "border-[#005BEA] ring-2 ring-[#005BEA]/15" : "border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="font-display font-semibold text-slate-900 truncate">
                        {l.name}
                      </div>
                      <span className={`text-[10px] tracking-[0.18em] uppercase font-bold ${
                        l.email_sent ? "text-emerald-600" : "text-amber-600"
                      }`}>
                        {l.email_sent ? "Envoyé" : "Non envoyé"}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 truncate">
                      {l.company || "—"} · {l.sector || "—"}
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      {formatDate(l.created_at)} · {l.locale?.toUpperCase()}
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            {/* Detail */}
            <div className="lg:col-span-7">
              {active ? (
                <article className="bg-white border border-slate-200 rounded-2xl p-7 lg:p-8 sticky top-24">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                      {active.name}
                    </h3>
                    <span className={`text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 rounded-full ${
                      active.email_sent
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}>
                      {active.email_sent ? "Email envoyé" : "Email NON envoyé"}
                    </span>
                  </div>

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-7">
                    <Detail icon={Mail} label="Email">
                      <a href={`mailto:${active.email}`} className="text-[#005BEA] hover:underline break-all">
                        {active.email}
                      </a>
                    </Detail>
                    <Detail icon={Phone} label="Téléphone">{active.phone || "—"}</Detail>
                    <Detail icon={Building2} label="Entreprise">{active.company || "—"}</Detail>
                    <Detail icon={Globe2} label="Secteur · Langue">
                      {(active.sector || "—")} · {(active.locale || "—").toUpperCase()}
                    </Detail>
                  </dl>

                  <div>
                    <div className="overline mb-2">Message</div>
                    <p className="whitespace-pre-wrap text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-4">
                      {active.message}
                    </p>
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <a
                      href={`mailto:${active.email}?subject=Re:%20Votre%20demande%20—%20klairys.fr`}
                      className="inline-flex items-center gap-2 bg-[#005BEA] hover:bg-[#0048b9] text-white px-5 h-11 rounded-full font-semibold text-sm transition-colors"
                    >
                      Répondre par email
                      <Mail className="w-4 h-4" />
                    </a>
                    <span className="text-xs text-slate-400 font-mono">ID · {active.id.slice(0, 8)}</span>
                  </div>
                </article>
              ) : (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl py-16 text-center text-slate-400 sticky top-24">
                  Sélectionnez un lead pour voir le détail.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-slate-900 font-medium text-sm">{children}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
